// src/auth/membershipHelpers.js
//
// Phase-1 helpers for the memberships/{uid} collection. Ticket 10 re-keys
// these docs to memberships/{uid}_{orgId} during the multi-tenant backfill;
// everything below uses the phase-1 uid-only key scheme.
//
// Identity fields (email, displayName) are denormalized onto memberships
// so that ticket 17 (member management) and ticket 19 (CSV export) can
// render per-member identity without reading users/{uid}, which strict
// rules (ticket 11) restrict to self + super admin.

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const MEMBERSHIPS = 'memberships';

export function normalizeMembershipRole(role) {
  if (role === 'admin') return 'orgAdmin';
  if (role === 'researcher') return 'reviewer';
  return role || null;
}

/**
 * Upserts memberships/{uid} with role + identity. Called from LoginPage's
 * self-heal fallback (for legacy config users the migration script
 * skipped) and by AdminConfigPage when an admin adds a new user.
 *
 * NOT the same as ticket 09's ensureMembership(uid, orgId, role, email,
 * displayName) — that helper writes the org-scoped {uid}_{orgId} shape.
 * This one is phase-1-only.
 */
export async function ensureLegacyMembership({
  uid,
  email,
  displayName = '',
  role,
  createdBy = null,
}) {
  if (!uid) throw new Error('ensureLegacyMembership requires uid');
  if (!role) throw new Error('ensureLegacyMembership requires role');
  const ref = doc(db, MEMBERSHIPS, uid);
  const snap = await getDoc(ref);
  const normalizedEmail = (email || '').toLowerCase();
  const base = {
    uid,
    email: normalizedEmail,
    displayName,
    role,
    lastLogin: serverTimestamp(),
  };
  if (!snap.exists()) {
    await setDoc(ref, {
      ...base,
      createdAt: serverTimestamp(),
      createdBy,
    });
  } else {
    await setDoc(ref, base, { merge: true });
  }
}

/**
 * Looks up memberships/{uid}. Returns the membership data or null if
 * missing. Used by LoginPage's new gate.
 */
export async function getMembership(uid) {
  const snap = await getDoc(doc(db, MEMBERSHIPS, uid));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    ...data,
    role: normalizeMembershipRole(data.role),
  };
}

/**
 * Returns all memberships with role === 'reviewer'. Function name
 * preserves the pre-ticket-11 "researcher" terminology for
 * backward-compat call-sites; callers can continue to use the name
 * even though the stored role value is now `reviewer`.
 */
export async function listResearchers() {
  const q = query(
    collection(db, MEMBERSHIPS),
    where('role', 'in', ['reviewer', 'researcher']),
  );
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => {
    const data = d.data();
    out.push({
      id: d.id,
      ...data,
      role: normalizeMembershipRole(data.role),
    });
  });
  out.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
  return out;
}

/**
 * Ticket 09: org-scoped membership upsert for the phase-2 `{uid}_{orgId}`
 * key scheme. Distinct from ensureLegacyMembership (phase-1, uid-only
 * key) — both helpers coexist during the transition and ticket 10's
 * backfill is the single place that re-keys legacy docs onto the new
 * scheme.
 *
 * Signature matches data-model doc §2.4: email and displayName are
 * required-at-create so the denormalized identity fields are populated
 * for ticket-17 member management and ticket-19 CSV export. `role`
 * must be one of 'orgAdmin' | 'reviewer'.
 */
export async function ensureMembership({
  uid,
  orgId,
  role,
  email,
  displayName = '',
  createdBy = null,
  inviteId = null,
}) {
  if (!uid || !orgId || !role) {
    throw new Error('ensureMembership requires uid, orgId, role');
  }
  if (!email) {
    throw new Error('ensureMembership requires email (denormalized onto the doc)');
  }
  if (role !== 'orgAdmin' && role !== 'reviewer') {
    throw new Error(`ensureMembership: role must be orgAdmin|reviewer (got ${role})`);
  }
  const id = `${uid}_${orgId}`;
  const ref = doc(db, MEMBERSHIPS, id);
  const mutablePayload = {
    uid,
    orgId,
    role,
    email: (email || '').toLowerCase(),
    displayName,
    lastLogin: serverTimestamp(),
  };
  if (inviteId) mutablePayload.inviteId = inviteId;

  // Preserve createdAt/createdBy on existing memberships. We avoid a
  // pre-read here because missing-doc gets can be denied by rules in
  // the signup/invite flows. Update first; if the doc doesn't exist
  // yet, fall back to a create-style set with the immutable fields.
  try {
    await updateDoc(ref, mutablePayload);
  } catch (err) {
    const code = err?.code || '';
    const missingDoc =
      code === 'permission-denied' ||
      code === 'not-found' ||
      code === 'failed-precondition' ||
      String(err?.message || '').includes('No document to update');
    if (!missingDoc) {
      throw err;
    }
    await setDoc(ref, {
      ...mutablePayload,
      createdAt: serverTimestamp(),
      createdBy,
    });
  }
  return id;
}

/**
 * Ticket 17: list all memberships for an org. Used by the member
 * management page. Relies on the permissive `list` rule on
 * memberships plus the client-side where('orgId', '==', orgId)
 * filter — the strict `get` rule still protects per-doc reads.
 */
export async function listMembershipsForOrg(orgId) {
  if (!orgId) return [];
  const q = query(
    collection(db, MEMBERSHIPS),
    where('orgId', '==', orgId),
  );
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => {
    const data = d.data();
    out.push({
      id: d.id,
      ...data,
      role: normalizeMembershipRole(data.role),
    });
  });
  out.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
  return out;
}

/**
 * Ticket 17: change a membership's role. Org admins only (rules).
 */
export async function updateMembershipRole(membershipId, nextRole) {
  if (!membershipId) throw new Error('updateMembershipRole requires membershipId');
  if (nextRole !== 'orgAdmin' && nextRole !== 'reviewer') {
    throw new Error(`updateMembershipRole: role must be orgAdmin|reviewer (got ${nextRole})`);
  }
  const ref = doc(db, MEMBERSHIPS, membershipId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`membership ${membershipId} not found`);
  await setDoc(
    ref,
    { role: nextRole },
    { merge: true },
  );
}

/**
 * Ticket 17: remove a membership. Org admins only (rules).
 */
export async function removeMembership(membershipId) {
  if (!membershipId) throw new Error('removeMembership requires membershipId');
  await deleteDoc(doc(db, MEMBERSHIPS, membershipId));
}
